import { Draggable } from '@hello-pangea/dnd';
import {
  AccessTime,
  Delete,
  DirectionsCar,
  Edit,
  Email,
  LocalShipping,
  Person,
  Phone,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { darken, useTheme } from '@mui/material/styles';
import { differenceInDays, differenceInHours, differenceInMinutes, formatDistanceToNow } from 'date-fns';
import { memo, useMemo } from 'react';
import { Lead, LeadStatus } from '@otw/models/leads/lead';
import { leadStatusTone } from 'components/StatusChip';
import { Vehicle } from '@otw/models/inventory/inventory';
import { useInventory } from 'hooks/inventory/useInventory';
import { usePermissions } from 'hooks/auth/usePermissions';
import { salesReps } from '@/constants';

interface LeadCardProps {
  lead: Lead;
  index: number;
  status: LeadStatus;
  onLeadSelect: (lead: Lead) => void;
  onViewDetails: (lead: Lead) => void;
  onViewSuggestedTrucks: (lead: Lead) => void;
  onDelete?: () => void;
  onUpdate?: (leadId: string, updates: Partial<Lead>) => Promise<void>;
  columnName?: string;
  columnColor?: string;
}

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  inProgress: 'In Progress',
  waitingFinance: 'Waiting Finance',
  closed: 'Closed',
  lost: 'Lost',
};

const getStatusLabel = (status: LeadStatus | string): string =>
  STATUS_LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1);

const getAccentColor = (theme: { custom: { status: Record<string, { fg: string }> } }, status: LeadStatus | string): string =>
  theme.custom.status[leadStatusTone(String(status))].fg

const toValidDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTimeDuration = (startDate: Date, endDate: Date = new Date()): string => {
  const days = differenceInDays(endDate, startDate);
  if (days > 0) return `${days}d`;
  const hours = differenceInHours(endDate, startDate);
  if (hours > 0) return `${hours}h`;
  const minutes = differenceInMinutes(endDate, startDate);
  return `${Math.max(minutes, 0)}m`;
};

const getTimeInStatus = (lead: Lead, columnStatus: LeadStatus | string): string => {
  if (!lead.statusHistory || lead.statusHistory.length === 0) {
    const createdAt = toValidDate(lead.createdAt);
    return createdAt ? formatTimeDuration(createdAt) : '—';
  }
  const sorted = [...lead.statusHistory].sort(
    (a, b) => (toValidDate(b.changedAt)?.getTime() || 0) - (toValidDate(a.changedAt)?.getTime() || 0),
  );
  const match = sorted.find((entry) => entry.newStatus === columnStatus);
  if (match) {
    const changedAt = toValidDate(match.changedAt);
    return changedAt ? formatTimeDuration(changedAt) : '—';
  }
  if (lead.status === columnStatus) return 'Just now';
  return '—';
};

const getInitials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const collectUnique = (
  truckRequest: Lead['truckRequest'] | undefined,
  field: 'type' | 'size',
): string[] => {
  if (!truckRequest?.types) return [];
  const all = truckRequest.types.flatMap((entry) => {
    const value = entry[field];
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
  });
  return [...new Set(all.filter((v) => v && v !== 'Any'))];
};

const LeadCard = memo(
  ({
    lead,
    index,
    status,
    onLeadSelect,
    onViewDetails,
    onViewSuggestedTrucks,
    onDelete,
    columnName,
    columnColor,
  }: LeadCardProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { vehicles } = useInventory('master_inventory');
    const { checkPermission } = usePermissions();
    const hasEditPermission = checkPermission('lead_management', 'modify');
    const hasDeletePermission = checkPermission('lead_management', 'delete');

    // Prefer the column's own color when provided (so custom columns drive the
    // theme of their cards) and fall back to the well-known status color map.
    const accent = columnColor || getAccentColor(theme, status);
    const createdAt = toValidDate(lead.createdAt);
    const updatedAt = toValidDate(lead.updatedAt);

    const attachedByTypeSize = useMemo(() => {
      if (!lead.truckIds?.length || !vehicles) return {};
      const attached = vehicles.filter((vehicle: Vehicle) => lead.truckIds?.includes(vehicle.id));
      const counts: Record<string, number> = {};
      attached.forEach((vehicle) => {
        if (vehicle.type) {
          const key = vehicle.size ? `${vehicle.type} ${vehicle.size}` : vehicle.type;
          counts[key] = (counts[key] || 0) + 1;
        } else {
          counts['Unknown'] = (counts['Unknown'] || 0) + 1;
        }
      });
      return counts;
    }, [lead, lead.truckIds, vehicles]);

    const assignedSalesRep = useMemo(
      () => salesReps.find((rep) => rep.id === lead.assignedRepId),
      [lead.assignedRepId],
    );

    const desiredTypes = useMemo(() => collectUnique(lead.truckRequest, 'type'), [lead.truckRequest]);
    const desiredSizes = useMemo(() => collectUnique(lead.truckRequest, 'size'), [lead.truckRequest]);
    const attachedEntries = useMemo(() => Object.entries(attachedByTypeSize), [attachedByTypeSize]);

    const handleCardClick = () => onViewDetails(lead);
    const stop = (handler: () => void) => (e: React.MouseEvent) => {
      e.stopPropagation();
      handler();
    };

    const repFullName =
      assignedSalesRep
        ? `${assignedSalesRep.first_name || ''} ${assignedSalesRep.last_name || ''}`.trim()
        : 'Unassigned';

    const leadNumberColor = useMemo(() => {
      if (theme.palette.mode === 'dark') return accent;
      try {
        return darken(accent, 0.2);
      } catch {
        return theme.palette.text.primary;
      }
    }, [accent, theme.palette.mode, theme.palette.text.primary]);

    const headerStatusLabel = columnName || getStatusLabel(status);
    const customerDisplayName = lead.customerName || 'Unknown';

    return (
      <Draggable draggableId={lead.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{ ...provided.draggableProps.style, marginBottom: 8 }}
          >
            <Paper
              variant="outlined"
              onClick={handleCardClick}
              sx={{
                p: isMobile ? 1.25 : 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                position: 'relative',
                borderLeft: `4px solid ${accent}`,
                bgcolor: snapshot.isDragging
                  ? alpha(accent, 0.08)
                  : theme.palette.background.paper,
                boxShadow: snapshot.isDragging
                  ? `0 8px 24px ${alpha(accent, 0.35)}`
                  : 'none',
                transform: snapshot.isDragging ? 'scale(1.01)' : 'none',
                transition:
                  'box-shadow 150ms ease, background-color 150ms ease, transform 150ms ease, border-color 150ms ease',
                '&:hover': {
                  boxShadow: theme.shadows[3],
                  borderColor: alpha(accent, 0.5),
                },
              }}
            >
              <Stack spacing={1.25}>
                {/* Header: avatar + name (wraps) + status chip pinned top-right */}
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Avatar
                    sx={{
                      bgcolor: alpha(accent, 0.18),
                      color: accent,
                      fontWeight: 700,
                      width: 36,
                      height: 36,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(lead.customerName || lead.company || '?')}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {lead.leadNumber?.trim() && (
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          color: leadNumberColor,
                          letterSpacing: 0.4,
                          mb: 0.25,
                        }}
                      >
                        {lead.leadNumber.trim()}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Tooltip title={customerDisplayName}>
                        <Box
                          component="span"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'block',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              lineHeight: 1.25,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {customerDisplayName}
                          </Typography>
                        </Box>
                      </Tooltip>
                      {/* <Tooltip title={headerStatusLabel}>
                        <Chip
                          size="small"
                          label={headerStatusLabel}
                          sx={{
                            flexShrink: 0,
                            maxWidth: 112,
                            minWidth: 0,
                            fontWeight: 700,
                            height: 22,
                            bgcolor: accent,
                            color: theme.palette.getContrastText(accent),
                            '& .MuiChip-label': {
                              px: 1,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      </Tooltip> */}
                    </Stack>
                    {lead.company && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          mt: 0.25,
                        }}
                      >
                        {lead.company}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {/* Contact line */}
                {(lead.email || lead.phone) && (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.5 }}
                  >
                    {lead.phone && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Phone sx={{ fontSize: 14 }} />
                        <Typography variant="caption" noWrap>
                          {lead.phone}
                        </Typography>
                      </Stack>
                    )}
                    {lead.email && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flexShrink: 1 }}>
                        <Email sx={{ fontSize: 14 }} />
                        <Typography variant="caption" noWrap>
                          {lead.email}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                )}

                {/* Assigned rep */}
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}
                  >
                    Rep
                  </Typography>
                  <Chip
                    size="small"
                    label={repFullName}
                    sx={{
                      height: 20,
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.dark,
                      '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                    }}
                  />
                </Stack>

                {/* Desired trucks */}
                {(desiredTypes.length > 0 || desiredSizes.length > 0) && (
                  <Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                      <DirectionsCar sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}
                      >
                        Wants
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {desiredTypes.map((t) => (
                        <Chip
                          key={`type-${t}`}
                          size="small"
                          label={t}
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      ))}
                      {desiredSizes.map((s) => (
                        <Chip
                          key={`size-${s}`}
                          size="small"
                          label={s}
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Attached trucks */}
                {attachedEntries.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                      <LocalShipping sx={{ fontSize: 14, color: 'success.main' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}
                      >
                        Attached
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {attachedEntries.map(([typeSize, count]) => (
                        <Chip
                          key={typeSize}
                          size="small"
                          label={`${typeSize} ×${count}`}
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: alpha(theme.palette.success.main, 0.12),
                            color: theme.palette.success.dark,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Time tracking */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.25 }}
                >
                  <AccessTime sx={{ fontSize: 14 }} />
                  <Typography variant="caption" noWrap>
                    Created{' '}
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {createdAt ? formatTimeDuration(createdAt) : '—'}
                    </Box>{' '}
                    ago
                  </Typography>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                  <Typography variant="caption" noWrap>
                    In {columnName || getStatusLabel(status)}{' '}
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {getTimeInStatus(lead, status)}
                    </Box>
                  </Typography>
                </Stack>

                <Divider sx={{ borderColor: alpha(theme.palette.text.primary, 0.06) }} />

                {/* Actions */}
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexGrow: 1, fontSize: '0.7rem' }}
                    noWrap
                  >
                    {updatedAt
                      ? `Updated ${formatDistanceToNow(updatedAt, { addSuffix: true })}`
                      : ''}
                  </Typography>


                  <Tooltip
                    title={hasEditPermission ? 'Edit lead' : 'You do not have permission to edit leads'}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={stop(() => onLeadSelect(lead))}
                        disabled={!hasEditPermission}
                        aria-label="Edit lead"
                        sx={{ p: 0.5 }}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {onDelete && (
                    <Tooltip
                      title={
                        hasDeletePermission ? 'Delete lead' : 'You do not have permission to delete leads'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={stop(onDelete)}
                          disabled={!hasDeletePermission}
                          sx={{ p: 0.5 }}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </div>
        )}
      </Draggable>
    );
  },
);

LeadCard.displayName = 'LeadCard';

export default LeadCard;
